import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Script from 'next/script';

import type { LcmUserSession } from '../auth/types/next-auth';
import { AnalyticEvents } from '../types/AnalyticEvents';
import { formatRolesToString } from '../utils/role';

const GA_TRACKING_ID = process.env.NEXT_PUBLIC_GA_ID ?? '';

type EventsEntities =
  | 'Nominations'
  | 'Evaluation'
  | 'Manager_Approval'
  | 'Admin'
  | 'Report'
  | 'My_Team_IDP'
  | 'IDP'
  | 'Results';

type GTagEvent = {
  action: AnalyticEvents;
  category: EventsEntities;
  label: string;
  value: number;
  session: LcmUserSession | null;
};

function fireEvent({ action, category, label, value, session }: GTagEvent) {
  if (!GA_TRACKING_ID || !session?.user) return;

  window.gtag?.('event', action, {
    event_category: category,
    event_label: label,
    value,
    user_properties: {
      country: session.user.country,
      employeeId: session.user.employeeId,
      zone: session.user.zone,
      band: session.user.band,
      roles: formatRolesToString(session.user.roles),
      assessmentType: session.user.surveyType,
    },
  });
}

function usePageView(session?: LcmUserSession | null) {
  const router = useRouter();

  useEffect(() => {
    function handleRouteChange(url: URL) {
      if (!GA_TRACKING_ID || !session?.user) return;

      if (!window.gtag) return;

      window.gtag('config', GA_TRACKING_ID, {
        page_path: url,
        user_properties: {
          country: session?.user.country,
          employeeId: session?.user.employeeId,
          zone: session?.user.zone,
          band: session?.user.band,
          roles: formatRolesToString(session?.user.roles),
          assessmentType: session?.user.surveyType,
        },
      });
    }

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, session?.user]);
}

type GoogleAnalyticsScriptProps = {
  session?: LcmUserSession | null;
};

function GoogleAnalyticsScript({ session }: GoogleAnalyticsScriptProps) {
  usePageView(session);

  if (!GA_TRACKING_ID || !session) return null;

  return (
    <>
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_TRACKING_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    </>
  );
}

export const GA = {
  fireEvent,
  Script: GoogleAnalyticsScript,
};
